import { useTheme } from '@mui/material';
import { NavLinkRenderProps, useLocation } from 'react-router-dom';
import { Badge } from '../Badge';
import { Box } from '../Box';
import {
  useSideNavExpanded,
  useSideNavLinkClick,
} from '../SideNavbar/SideNavContext';
import {
  ItemWrapper,
  LabelContainer,
  NavItemWrapper,
  StyledBox,
  StyledNavItemLabel,
  StyledTopLevelNavLink,
} from './NavItem.styles';
import { NavItemGroup } from './NavItemGroup';
import { NavItemProps } from './types';
import { getNavIconColors } from './utils';

export function NavItem({
  kind = 'dark',
  label,
  to,
  icon,
  subItems,
  counter,
  activeUrl,
  activePathPrefix,
  activeColor,
  activeSecondaryColor,
  iconBadge,
  iconBadgeTestId,
  onClick,
  bigMargins = false,
  visible = true,
}: NavItemProps) {
  const theme = useTheme();
  const expanded = useSideNavExpanded();
  const location = useLocation();
  const onLinkClick = useSideNavLinkClick();

  if (!visible) {
    return null;
  }

  if (subItems) {
    return (
      <NavItemGroup
        activeColor={activeColor}
        activePathPrefix={activePathPrefix}
        activeSecondaryColor={activeSecondaryColor}
        icon={icon}
        iconBadge={iconBadge}
        iconBadgeTestId={iconBadgeTestId}
        label={label}
        subItems={subItems}
      />
    );
  }

  if (!to) {
    throw new Error('You must provide a either `subItems` or `to` props.');
  }

  const navLabel = label ? (
    <LabelContainer aria-hidden={!expanded} expanded={expanded}>
      <StyledNavItemLabel variant="bodyStrong">{label}</StyledNavItemLabel>
    </LabelContainer>
  ) : null;
  const counterBadge = counter ? (
    <Box>
      <Badge color="error" count={counter} size="large" />
    </Box>
  ) : null;
  const renderContent = ({ isActive }: NavLinkRenderProps) => {
    const active =
      isActive || (activeUrl ? location.pathname.includes(activeUrl) : false);
    const labelColor = active
      ? theme.palette.text.primaryInverted
      : theme.palette.text.secondaryInverted;
    const { color, primaryColor, secondaryColor } = getNavIconColors({
      theme,
      active,
      labelColor,
      activeColor,
      activeSecondaryColor,
    });

    return (
      <ItemWrapper color={labelColor}>
        <StyledBox
          color={color}
          expanded={expanded}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
        >
          {icon}
        </StyledBox>
        {navLabel}
        {counterBadge}
      </ItemWrapper>
    );
  };

  const handleClick = () => {
    onClick?.();
    onLinkClick?.();
  };

  return (
    <NavItemWrapper bigMargins={bigMargins} kind={kind}>
      <StyledTopLevelNavLink
        aria-label={expanded ? undefined : label}
        end
        onClick={handleClick}
        to={to}
      >
        {renderContent}
      </StyledTopLevelNavLink>
    </NavItemWrapper>
  );
}
